// 03 §18 machines — product, costing, materials. Permission names follow the 02 §8.3 catalogue with
// the 04 §16 #13 / 06 §16 #16 alignment (dev.approve→development.approve, samples.edit→
// development.edit, materials.edit→testing.edit, materials.release_hold→lots.release_hold).
import { allExcept, defineMachine, systemStep as D, userStep as U } from './machine.ts';
import type { EnumValue } from './define.ts';

const STYLE = ['development', 'costing', 'orderable', 'discontinued'] as const;
export const STYLE_LIFECYCLE = defineMachine(
  'product.style.lifecycle_status',
  STYLE,
  ['development'],
  [
    U('development', 'costing', 'to_costing', 'styles.edit'),
    D('costing', 'orderable', 'cost_version_approved'),
    D('orderable', 'costing', 'approved_version_withdrawn'),
    U(allExcept(STYLE, 'discontinued'), 'discontinued', 'discontinue', 'styles.delete'),
  ],
);
export type StyleLifecycleStatus = EnumValue<typeof STYLE_LIFECYCLE.states>;

export const STYLE_SPEC = defineMachine(
  'product.style_spec.status',
  ['draft', 'submitted', 'approved', 'superseded', 'cancelled'],
  ['draft'],
  [
    U('draft', 'submitted', 'submit', 'specs.edit'),
    U('submitted', 'approved', 'approve', 'specs.approve', { sod: true }),
    U('submitted', 'draft', 'reject', 'specs.approve', { reason: true }),
    U('draft', 'cancelled', 'cancel', 'specs.edit'),
    D('approved', 'superseded', 'superseded_by_newer'),
  ],
);

export const BOM_REVISION = defineMachine(
  'product.bom_revision.status',
  ['draft', 'released', 'superseded'],
  ['draft'],
  [
    U('draft', 'released', 'release', 'styles.release_bom'),
    D('released', 'superseded', 'superseded_by_newer'),
  ],
);

export const SAMPLE = defineMachine(
  'product.sample.status',
  [
    'requested',
    'in_work',
    'ready',
    'sent',
    'at_customer',
    'returned',
    'consumed',
    'lost',
    'cancelled',
  ],
  ['requested'],
  [
    U('requested', 'in_work', 'start', 'development.edit'),
    U('in_work', 'ready', 'mark_ready', 'development.edit'),
    U('ready', 'sent', 'send', 'development.edit'),
    U('sent', 'at_customer', 'mark_at_customer', 'development.edit'),
    U('at_customer', 'returned', 'mark_returned', 'development.edit'),
    U(['ready', 'returned'], 'consumed', 'consume', 'development.edit'),
    U(['sent', 'at_customer'], 'lost', 'mark_lost', 'development.edit', { reason: true }),
    U(['requested', 'in_work', 'ready'], 'cancelled', 'cancel', 'development.edit'),
  ],
);

export const DEV_APPROVAL = defineMachine(
  'product.dev_approval.decision',
  ['pending', 'approved', 'approved_with_changes', 'rejected', 'cancelled'],
  ['pending'],
  [
    U('pending', 'approved', 'approve', 'development.approve', { sod: true }),
    U('pending', 'approved_with_changes', 'approve_with_changes', 'development.approve', {
      sod: true,
    }),
    U('pending', 'rejected', 'reject', 'development.approve', { sod: true, reason: true }),
    U('pending', 'cancelled', 'cancel', 'development.edit'),
  ],
);

/** submit uses `costing.submit` (02 §8.3 special action; 03 wrote costing.edit). */
export const COST_SHEET_VERSION = defineMachine(
  'costing.cost_sheet_version.status',
  ['draft', 'submitted', 'approved', 'rejected', 'superseded'],
  ['draft'],
  [
    U('draft', 'submitted', 'submit', 'costing.submit'),
    U('submitted', 'approved', 'approve', 'costing.approve', { sod: true }),
    U('submitted', 'rejected', 'reject', 'costing.approve', { reason: true }),
    // CR-W2b-12 (CR-W2b-2 R1): 04 §11.6 the owner reworks a rejected version.
    U('rejected', 'draft', 'revise', 'costing.edit'),
    D('approved', 'superseded', 'superseded_by_newer'),
  ],
);

export const ACTUAL_COST = defineMachine(
  'costing.actual_cost.status',
  ['open', 'closed', 'reopened'],
  ['open'],
  [
    U(['open', 'reopened'], 'closed', 'close', 'costing.close'),
    U('closed', 'reopened', 'reopen', 'costing.reopen', { reason: true }),
  ],
);

export const LANDED_COST_CHARGE = defineMachine(
  'costing.landed_cost_charge.status',
  ['draft', 'allocated', 'posted', 'cancelled'],
  ['draft'],
  [
    U('draft', 'allocated', 'allocate', 'costing.edit'),
    U('allocated', 'posted', 'post', 'costing.post'),
    U('draft', 'cancelled', 'cancel', 'costing.edit'),
  ],
);

/** `materials.reject` (03) → `testing.approve` (release/reject a result, 02 §8.3) [assumption]. */
/**
 * CR-W2a-39 R-ENUM-1 (BR-TST-002/004/008 as built): a quarantined lot can be rejected on a failed test and moves to /
 * from on_hold with manual holds while its test is still pending.
 */
export const LOT_QUALITY = defineMachine(
  'materials.lot.quality_status',
  ['quarantine', 'available', 'on_hold', 'rejected', 'consumed'],
  ['quarantine'],
  [
    D('quarantine', 'available', 'test_passed_or_not_required'),
    D(['available', 'quarantine'], 'on_hold', 'hold_placed'),
    D('on_hold', 'available', 'holds_released'),
    D('on_hold', 'quarantine', 'holds_released_test_pending'),
    U(['on_hold', 'quarantine'], 'rejected', 'reject', 'testing.approve', { reason: true }),
    D('available', 'consumed', 'depleted'),
    // CR-W2b-12 (CR-W2b-3 R-LQ-1): every roll of the lot was reversed with its goods receipt; the lot is closed.
    D(['quarantine', 'available', 'on_hold'], 'consumed', 'receipt_reversed'),
  ],
);

/** + `reversed` (goods-receipt reversal, BUILD-PLAN §9 / 05). */
export const ROLL = defineMachine(
  'materials.roll.status',
  ['available', 'reserved', 'on_hold', 'consumed', 'returned', 'written_off', 'reversed'],
  ['available'],
  [
    D('available', 'reserved', 'reserved'),
    D('reserved', 'available', 'unreserved'),
    D(['available', 'reserved'], 'on_hold', 'hold_placed'),
    D('on_hold', 'available', 'hold_released'),
    D('reserved', 'consumed', 'issued_to_zero'),
    D('available', 'consumed', 'split_into_children'),
    D('available', 'returned', 'supplier_note_posted'),
    D('available', 'written_off', 'write_off_posted'),
    D('available', 'reversed', 'receipt_reversed'),
  ],
);

/** Release of a `test_failed` hold additionally needs an override (checked by the lots lane). */
export const QUALITY_HOLD = defineMachine(
  'materials.quality_hold.status',
  ['active', 'released', 'disposed'],
  ['active'],
  [
    U('active', 'released', 'release', 'lots.release_hold', { reason: true }),
    D('active', 'disposed', 'disposal_posted'),
  ],
);

export const FABRIC_TEST = defineMachine(
  'materials.fabric_test.status',
  ['requested', 'in_lab', 'result_received', 'closed', 'cancelled'],
  ['requested'],
  [
    U('requested', 'in_lab', 'send', 'testing.edit'),
    U('in_lab', 'result_received', 'record_result', 'testing.edit'),
    U('result_received', 'closed', 'decide', 'testing.approve'),
    // CR-W2a-39 R-ENUM-1: a pass closes the test without a decision (BR-TST-004).
    D('result_received', 'closed', 'result_passed'),
    U('requested', 'cancelled', 'cancel', 'testing.edit'),
  ],
);
